import { useEffect, useMemo } from "react";
import { noop } from "lodash";

import { SelectionFeature, SelectionType } from "src/js/stores/map/store";

import { useDesignationsByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationsByGeometry";
import { apiDesignationIsVisible } from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext/utils";
import { LayerTypePrintableProps } from "react-migration/layouts/map/Multilayer/types";
import {
  ConstraintLayerConfig,
  isSingleConstraintLayer,
} from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { DetailView } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/TwoColumnDetailSelectionLayout/DetailView";
import { useLayer } from "react-migration/layouts/map/Multilayer/hooks/useLayer";
import { Loading } from "react-migration/components/Loading";
import {
  useConstraintsLayerTypeContext,
  ConstraintsLayerTypeProvider,
} from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext";

type PrintableDetailViewProps = {
  designation: SingleDesignation;
  parentSelection: SelectionFeature;
};

function PrintableDetailView({ designation, parentSelection }: Readonly<PrintableDetailViewProps>) {
  const detailSelection = {
    id: designation.id,
    type: SelectionType.DESIGNATION,
  };

  // @todo stop using DetailView here, and instead use the SelectionDetailView from the useSelectionHandler hook
  return (
    <DetailView
      detailSelection={detailSelection}
      selection={parentSelection}
      setSelection={noop}
      setDetailSelection={noop}
      onBack={noop}
    />
  );
}

const AmenitiesPrintableInner = ({ selection, layer, onLoaded, onUnmount }: LayerTypePrintableProps) => {
  const { t } = useTranslation();
  const { constraintsStore } = useConstraintsLayerTypeContext();
  const layerStore = constraintsStore.layers[layer.id];

  const layerConfig = layer.layerConfig as ConstraintLayerConfig;
  const rootCategories = layerConfig
    ? isSingleConstraintLayer(layerConfig)
      ? [layerConfig.rootCategory]
      : layerConfig.rootCategories
    : ["amenities"];
  const intersectionBuffer = layerConfig?.intersectionThresholdMeters;

  const { designations, loading } = useDesignationsByGeometry({
    geometry: selection.feature?.geometry ?? null,
    bufferMeters: intersectionBuffer,
    rootCategories,
  });

  const visibleDesignations = useMemo(
    () =>
      designations
        .filter((designation) => apiDesignationIsVisible(designation, layerStore))
        .map((designation) => ({
          ...designation,
          designation_document_sections: designation.designation_document_sections ?? [],
          designation_attributes: designation.designation_attributes ?? [],
          source: {
            ...designation.source,
            documents: designation.source?.documents ?? [],
          },
        }))
        .sort((a, b) => {
          const categoryA = a.sub_category?.display_name ?? "";
          const categoryB = b.sub_category?.display_name ?? "";

          if (categoryA !== categoryB) {
            return categoryA.localeCompare(categoryB);
          }

          // The callback always returns a number by providing a fallback value
          const nameA = a.display_name ?? "";
          const nameB = b.display_name ?? "";
          return nameA.localeCompare(nameB);
        }),
    [designations, layerStore]
  );

  useEffect(() => {
    if (!loading) {
      onLoaded();
    }
  }, [loading, onLoaded]);

  useEffect(() => {
    return () => {
      onUnmount();
    };
  }, [onUnmount]);

  if (loading) {
    return (
      <div data-testid="amenities-printable-loading" className="mb-4">
        <h3>{t("site_report.layers.amenities")}</h3>
        <Loading />
      </div>
    );
  }

  if (!visibleDesignations.length) {
    return null;
  }

  return (
    <div className="amenities-printable mb-4">
      <h3 className="text-lg font-bold mb-4">{t("site_report.layers.amenities")}</h3>
      <div className="space-y-4">
        {visibleDesignations.map((designation) => (
          <div key={designation.id} className="print:break-inside-avoid">
            <PrintableDetailView designation={designation} parentSelection={selection} />
          </div>
        ))}
      </div>
    </div>
  );
};

function withConstraintsContext(Component: React.ComponentType<LayerTypePrintableProps>) {
  return function ConstraintsContextWrapper({ layer: layerProp, ...props }: LayerTypePrintableProps) {
    const layer = useLayer(layerProp);

    const [layers, visibleLayerIds] = useMemo(() => [[layer], [layer.id]], [layer]);

    return (
      <ConstraintsLayerTypeProvider
        id={`amenities-printable-${layer.id}`}
        layers={layers}
        visibleLayerIds={visibleLayerIds}
        selection={props.selection}
        setSelection={noop}
        setDetailSelection={noop}
      >
        <Component {...props} layer={layer} />
      </ConstraintsLayerTypeProvider>
    );
  };
}

const AmenitiesPrintable = withConstraintsContext(AmenitiesPrintableInner);

export default AmenitiesPrintable;
